import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import BackButton from '../BackButton/BackButton';
import Loading from '../Loading/Loading';
import SaveButton from '../SaveButton/SaveButton';
import SaveSnackbar from '../SaveSnackbar/SaveSnackbar';

import api from '../../app/api';
import * as Types from '../../app/types';

const emptyCategoryGroup: Types.CategoryGroup = {
  id: 0, name: '', slug: ''
};

const CategoryGroup = () => {

  // define state

  const [status, setStatus] = useState<Types.ComponentState>('loading');
  const [saveState, setSaveState] = useState<Types.SaveState>('closed');
  const [categoryGroup, setCategoryGroup] = useState<Types.CategoryGroup|null>(null);
  const urlParams = useParams();

  // initialize

  useEffect(() => {
    (async () => {
      const loadedCategoryGroup = urlParams.slug
        ? await api.getCategoryGroup(urlParams.slug)
        : emptyCategoryGroup;
      if (loadedCategoryGroup) {
        setCategoryGroup(loadedCategoryGroup);
      }
      setStatus('complete');
    })();
  }, [urlParams]);

  // define validation

  const [formErrors, setFormErrors] = useState<Types.FormErrors>({});

  const updateCategoryGroup = (field: string) => (e: any) => {
    setFormErrors(e => ({ ...e, [field]: '' }));
    setCategoryGroup(g => ({ ...g, [field]: e.target.value } as Types.CategoryGroup));
  };

  const isValid = (categoryGroup: Types.CategoryGroup): boolean => {
    if (!categoryGroup) { return false; }
    const errors: Types.FormErrors = {};
    if (!categoryGroup.name.trim()) {
      errors.name = 'Please enter a name';
    }
    setFormErrors(errors);
    return !Object.keys(errors).length;
  };

  // render

  return (
    <>
      { status === 'loading' && (
        <Loading />
      )}
      { status === 'complete' && (
        <>

          { !!categoryGroup && (
            <>

              <Stack direction="row" sx={{ pb: 2 }} spacing={4} justifyContent="center">
                <Box>
                  <BackButton />
                </Box>
                <Box>
                  <SaveButton onClick={async () => {
                    if (saveState === 'info' || !isValid(categoryGroup)) { return; }
                    setSaveState('info');
                    const result = await (categoryGroup?.id
                      ? api.updateCategoryGroup(categoryGroup)
                      : api.createCategoryGroup(categoryGroup)
                    );
                    if (result && !result.error) {
                      if (!categoryGroup?.id) { setCategoryGroup(emptyCategoryGroup); }
                      setSaveState('success');
                    } else {
                      if (result?.error?.field && result.error.message) {
                        setFormErrors(e => ({ ...e, [result.error.field]: result.error.message }));
                      }
                      setSaveState('error');
                    }
                  }} />
                </Box>
              </Stack>

              <Grid container spacing={4} justifyContent="center">

                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="button">Category Group Details</Typography>
                      <TextField label="Name" value={categoryGroup.name} required
                        error={!!formErrors['name']} helperText={formErrors['name']}
                        onChange={updateCategoryGroup('name')} fullWidth />
                      <TextField label="Slug" value={categoryGroup.slug}
                        error={!!formErrors['slug']} helperText={formErrors['slug']}
                        onChange={updateCategoryGroup('slug')} fullWidth />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <SaveSnackbar state={saveState} setState={setSaveState} />
            </>
          )}

          { !categoryGroup && (
            <>
              <div style={{ textAlign: 'center' }}>The category group was not found</div>
              <Stack direction="row" justifyContent='center' sx={{ pt: 4 }}>
                <BackButton />
              </Stack>
            </>
          )}

        </>
      )}
    </>
  );

};

export default CategoryGroup;
